// engine-exception: workspace settings are one row per tenant (GET /v1/settings answers defaults when absent; PATCH upserts) — the singleton shape fits neither list/get/create nor a ScreenDef kind
import { useQueryClient } from '@tanstack/react-query';
import { Field, Select, Skeleton, ErrorBanner, PageHeader, useApi, useApiQuery, useToast } from '../../engine';
import { ApiError } from '../../engine/api/client';
import { HaloConnectionCard } from './ConnectedSystemCard';
import { IntegrationStatusCard } from './IntegrationStatusCard';

// Workspace settings, in plain words. Each knob changes enforced behaviour (the DB functions
// read these), so a change applies immediately and everywhere. GET always answers (absent
// row = defaults) so there is no 404 branch.

interface Settings {
  timesheet_cycle: 'weekly' | 'monthly';
  week_starts_on: number;
  timesheet_approval: 'none' | 'required';
  require_locked_time_to_invoice: boolean;
  require_mfa: boolean;
}
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function SettingsScreen() {
  const api = useApi();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useApiQuery<Settings>(['settings'], '/v1/settings');
  const s = q.data;

  const patch = async (body: Partial<Settings>) => {
    try {
      await api.request('/v1/settings', { method: 'PATCH', body });
      void qc.invalidateQueries({ queryKey: ['settings'] });
      void qc.invalidateQueries({ queryKey: ['res', 'timesheets'] });
      toast({ title: 'Settings saved', detail: 'Applies to new periods immediately.' });
    } catch (e) {
      toast({ kind: 'error', title: 'Could not save settings', detail: e instanceof ApiError ? e.detail : String(e) });
    }
  };

  return (
    <div className="hf-page" style={{ maxWidth: 720 }}>
      <PageHeader title="Settings" subtitle="How this workspace runs its time and billing." />
      {q.isLoading ? (
        <Skeleton lines={6} />
      ) : q.error ? (
        <ErrorBanner error={q.error} />
      ) : s ? (
        <div className="hf-stack" style={{ gap: 'var(--s-5)' }}>
          <section className="hf-card pad hf-stack" aria-label="Sign-in security">
            <div className="hf-caps">Sign-in security</div>
            <p className="hf-muted" style={{ margin: 0, fontSize: 'var(--t-13)' }}>With this on, everyone must verify a code from an authenticator app before using the workspace; people without one are asked to set it up. Changing it asks you for your own code. API keys are not affected.</p>
            <Field label="Require two-factor authentication for everyone" id="set-mfa">
              <Select id="set-mfa" value={s.require_mfa ? 'on' : 'off'} onChange={(e) => void patch({ require_mfa: e.target.value === 'on' })} options={[{ value: 'off', label: 'Off — two-factor only for sensitive changes' }, { value: 'on', label: 'On — everyone, every session' }]} />
            </Field>
          </section>

          <section className="hf-card pad hf-stack">
            <div className="hf-caps">Timesheet periods</div>
            <p className="hf-muted" style={{ margin: 0, fontSize: 'var(--t-13)' }}>Nobody creates timesheets by hand — logged time lands in the period covering its date. This controls the period shape.</p>
            <div className="hf-row" style={{ gap: 'var(--s-3)', flexWrap: 'wrap' }}>
              <Field label="Period length" id="set-cycle">
                <Select id="set-cycle" value={s.timesheet_cycle} onChange={(e) => void patch({ timesheet_cycle: e.target.value as Settings['timesheet_cycle'] })} options={[{ value: 'weekly', label: 'Weekly' }, { value: 'monthly', label: 'Monthly' }]} />
              </Field>
              {s.timesheet_cycle === 'weekly' && (
                <Field label="Week starts on" id="set-weekstart">
                  <Select id="set-weekstart" value={String(s.week_starts_on)} onChange={(e) => void patch({ week_starts_on: Number(e.target.value) })} options={WEEKDAYS.map((d, i) => ({ value: String(i), label: d }))} />
                </Field>
              )}
            </div>
          </section>

          <section className="hf-card pad hf-stack">
            <div className="hf-caps">Invoicing</div>
            <p className="hf-muted" style={{ margin: 0, fontSize: 'var(--t-13)' }}>Time is editable until it is committed — by its period closing, or by landing on an invoice. This controls whether invoicing may come first.</p>
            <Field label="Invoice only reviewed time" id="set-gate">
              <Select id="set-gate" value={s.require_locked_time_to_invoice ? 'on' : 'off'} onChange={(e) => void patch({ require_locked_time_to_invoice: e.target.value === 'on' })} options={[{ value: 'off', label: 'Off — any unbilled time can be invoiced' }, { value: 'on', label: 'On — only time from closed periods' }]} />
            </Field>
          </section>

          <section className="hf-card pad hf-stack">
            <div className="hf-caps">Timesheet approval</div>
            <p className="hf-muted" style={{ margin: 0, fontSize: 'var(--t-13)' }}>With approval on, a period must be submitted and approved before it can be locked — enforced by the database, not just the buttons. Solo shops usually leave this off and lock directly.</p>
            <Field label="Require approval before locking" id="set-approval">
              <Select id="set-approval" value={s.timesheet_approval} onChange={(e) => void patch({ timesheet_approval: e.target.value as Settings['timesheet_approval'] })} options={[{ value: 'none', label: 'Off — lock directly' }, { value: 'required', label: 'On — submit → approve → lock' }]} />
            </Field>
          </section>
        </div>
      ) : null}
      <div style={{ marginTop: 'var(--s-5)' }}>
        <IntegrationStatusCard />
      </div>
      <div style={{ marginTop: 'var(--s-5)' }}>
        <HaloConnectionCard />
      </div>
    </div>
  );
}
